import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { DirectionService } from '../../direction.service';
import { WallApiService } from '../../wall/wall-api.service';
import { DialogService } from '../dialog.service';

export interface WallContactDialogData {
  userID: string;
  authorName?: string;
}

@Component({
  selector: 'app-wall-contact-dialog',
  templateUrl: './wall-contact-dialog.component.html',
  styleUrls: ['./wall-contact-dialog.component.css'],
  host: { class: 'fill-screen-modal-phone' },
})
export class WallContactDialogComponent implements OnInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  message = '';
  isSending = false;
  isSent = false;
  readonly maxLength = 1000;

  private directionSub?: Subscription;
  private darkModeSub?: Subscription;
  private sendSub?: Subscription;

  constructor(
    private directionService: DirectionService,
    private dialogService: DialogService,
    private wallApi: WallApiService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<WallContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WallContactDialogData
  ) {}

  get authorName(): string {
    return (
      String(this.data?.authorName || '').trim() ||
      this.translate.instant('wall.personal.anonymous')
    );
  }

  get titleName(): string {
    const name = this.authorName;
    if (name.length <= 15) {
      return name;
    }
    return `${name.slice(0, 15)}...`;
  }

  ngOnInit(): void {
    this.directionSub = this.directionService.direction$.subscribe((direction) => {
      this.isRTL = direction === 'rtl';
    });
    this.darkModeSub = this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
  }

  ngOnDestroy(): void {
    this.directionSub?.unsubscribe();
    this.darkModeSub?.unsubscribe();
    this.sendSub?.unsubscribe();
  }

  canSend(): boolean {
    return !this.isSending && !this.isSent && !!this.message.trim() && !!this.data?.userID;
  }

  closeDialog(): void {
    if (this.isSending) {
      return;
    }
    this.dialogService.onCloseWallContactDialog();
  }

  onSend(): void {
    if (!this.canSend()) {
      return;
    }
    this.isSending = true;
    this.dialogRef.disableClose = true;
    this.sendSub?.unsubscribe();
    this.sendSub = this.wallApi
      .sendWallContact(this.data.userID, this.message.trim())
      .subscribe({
        next: () => {
          this.isSending = false;
          this.dialogRef.disableClose = false;
          this.isSent = true;
        },
        error: (err) => {
          this.isSending = false;
          this.dialogRef.disableClose = false;
          const code = String(err?.error?.message || '');
          const keyByCode: { [k: string]: string } = {
            CONTACT_NOT_ALLOWED: 'wall.contact.error-not-allowed',
            CONTACT_SELF: 'wall.contact.error-self',
            INVALID_CONTACT_TEXT: 'wall.contact.error-empty',
            EMAIL_SENDING_ERROR: 'wall.contact.error-email',
            RECIPIENT_EMAIL_MISSING: 'wall.contact.error-no-recipient',
          };
          console.error('WALL_CONTACT_FAILED', code, err?.error);
          const details =
            code === 'EMAIL_SENDING_ERROR' && typeof err?.error?.details === 'string'
              ? ` ${err.error.details}`
              : '';
          this.snackBar.open(
            this.translate.instant(keyByCode[code] || 'wall.contact.error') + details,
            '',
            { duration: 5000, verticalPosition: 'top', panelClass: ['auto-width', 'center-text'] }
          );
        },
      });
  }
}
